import React, { useCallback, useEffect } from "react";
import { useDispatch } from "react-redux";
import { openWindow } from "../Slices/windowsSlice";
import WindowLayer from "./windowManager/WindowLayer";
import WindowContent from "./windowManager/WindowContent";
import DesktopIcons from "./desktop/DesktopIcons";
import StickyNotesLayer from "./desktop/StickyNotesLayer";
import Taskbar from "./taskbar/Taskbar";
import { useDesktopLayoutSync } from "./layout/useDesktopLayoutSync";
import { useOpenWindowsSync } from "./layout/useOpenWindowsSync";
import { useRcAboveTaskbar } from "./useRcAboveTaskbar";
import { useWebtopBootstrap } from "./useWebtopBootstrap";
import WebtopGlobalListeners from "./WebtopGlobalListeners";
import WebtopLayoutContext from "./layout/WebtopLayoutContext";
import { getWallpaperCss, getAccent, getTheme } from "./layout/layoutModel";
import { clearSaveError } from "./layout/desktopLayoutSlice";
import { superAdminRoutes } from "../Routes/Appcontainer";
import NotepadApp from "./desktop/NotepadApp";
import FileExplorerApp from "./files/FileExplorerApp";
import RecycleBinApp from "./files/RecycleBinApp";
import SheetsApp from "./sheets/SheetsApp";
import DesktopFiles from "./files/DesktopFiles";
import TearoffBar from "./TearoffBar";
import { useMainWindowBridge, sendReattach } from "./useTearoffBridge";
import "./windowManager/webtop.css";

// Window routes for the webtop = the app routes + built-in shell-app windows
// (e.g. Notepad, Files, Recycle Bin). Module-level constant so the reference is
// stable — keeps the memoized WindowContent from re-rendering screens (Phase 15).
const WEBTOP_WINDOW_ROUTES = [
  ...superAdminRoutes,
  { id: "wt-notepad", path: "webtop-notepad", element: <NotepadApp /> },
  { id: "wt-files", path: "webtop-files", element: <FileExplorerApp /> },
  { id: "wt-recycle-bin", path: "webtop-recycle-bin", element: <RecycleBinApp /> },
  { id: "wt-sheets", path: "webtop-sheets", element: <SheetsApp /> },
];

// Parsed once at module level — URL params don't change after page load.
const _params = new URLSearchParams(window.location.search);
const IS_TEAROFF = _params.get("tearoff") === "1";
const TEAROFF_DATA = (() => {
  if (!IS_TEAROFF) return null;
  try { return JSON.parse(decodeURIComponent(_params.get("tdata") || "null")); }
  catch { return null; }
})();

const WebtopPreview = () => {
  const dispatch = useDispatch();

  // ── Main window: listen for reattach requests from tearoff windows ──────────
  // (no-op in tearoff windows; safe to call unconditionally)
  useMainWindowBridge();

  // ── Tearoff window: open the detached view and wire up auto-reattach ────────
  useEffect(() => {
    if (!TEAROFF_DATA?.path) return;
    dispatch(openWindow({ path: TEAROFF_DATA.path, title: TEAROFF_DATA.title || "" }));
  }, [dispatch]);

  // Web mode: if the tearoff popup is closed via the OS X button, reattach.
  useEffect(() => {
    if (!IS_TEAROFF || !TEAROFF_DATA?.path) return;
    const onPageHide = () => sendReattach(TEAROFF_DATA.path, TEAROFF_DATA.title || "");
    window.addEventListener("pagehide", onPageHide);
    return () => window.removeEventListener("pagehide", onPageHide);
  }, []);

  // Load global app data (user, access, leads, clients, mail list, …) the same
  // way AppContainer does — the webtop is outside the tab system, so a hard
  // reload on /webtop would otherwise leave every data-driven screen empty.
  useWebtopBootstrap();

  // Load the user's saved layout on mount (Phase 7); customization reads/writes it.
  const sync = useDesktopLayoutSync();
  const { layout, saveError, updateLayout } = sync;
  const dismissSaveError = useCallback(() => dispatch(clearSaveError()), [dispatch]);

  // Persist open windows to the layout so they survive page refreshes.
  // Skip in tearoff windows — they have their own independent state.
  useOpenWindowsSync(IS_TEAROFF ? null : updateLayout);

  // Keep the RingCentral widget above the taskbar (Rule 7 — reposition, not z-index).
  useRcAboveTaskbar();

  const rootStyle = {
    background: getWallpaperCss(layout),
    "--wt-accent": getAccent(layout),
  };

  return (
    <WebtopLayoutContext.Provider value={sync}>
      <div className={`webtop-root webtop-theme-${getTheme(layout)}`} style={rootStyle}>
        {/* Tearoff indicator bar: visible only in detached OS windows */}
        {IS_TEAROFF && TEAROFF_DATA && (
          <TearoffBar path={TEAROFF_DATA.path} title={TEAROFF_DATA.title || ""} />
        )}

        {saveError && (
          <div className="webtop-notice webtop-notice--error" role="alert" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
            <span>Couldn't save your desktop changes — retrying automatically.</span>
            <button
              onClick={dismissSaveError}
              style={{ background: "none", border: "none", cursor: "pointer", color: "inherit", fontSize: 16, lineHeight: 1, padding: "0 4px", opacity: 0.7 }}
              aria-label="Dismiss"
            >✕</button>
          </div>
        )}

        <div className="webtop-stage">
          <DesktopIcons />
          <DesktopFiles />
          <StickyNotesLayer />
          <WindowLayer
            renderContent={(win) => <WindowContent win={win} routes={WEBTOP_WINDOW_ROUTES} />}
          />
        </div>

        <Taskbar />

        <WebtopGlobalListeners />
      </div>
    </WebtopLayoutContext.Provider>
  );
};

export default WebtopPreview;
